'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Home, Image, Code, Search, FileText } from 'lucide-react';
import { localizedHref } from '@/lib/i18n/href';
import { defaultLocale, isValidLocale, type Locale } from '@/lib/i18n/config';

// Split out of not-found.tsx because Next.js doesn't allow exporting
// `metadata` from a 'use client' file, and `not-found.tsx` (a Next.js
// special file) doesn't receive route `params` the way page.tsx does — it
// can be triggered from any nested route, so there's no locale segment to
// read from props. We detect locale from the URL instead, the same
// convention middleware.ts uses: no prefix = default locale, /<locale>
// prefix = that locale — checked generically against `locales` in
// lib/i18n/config.ts rather than hardcoding a specific locale code, so
// adding a locale there doesn't also require updating this file.

const T = {
  en: {
    title: 'Page Not Found',
    body: "We're sorry, but the page you are looking for doesn't exist, has been removed, or is temporarily unavailable.",
    returnHome: 'Return to Homepage',
    popularTools: 'Popular Tools',
  },
  ar: {
    title: 'الصفحة غير موجودة',
    body: 'نعتذر، الصفحة التي تبحث عنها غير موجودة أو تم حذفها أو أنها غير متاحة مؤقتًا.',
    returnHome: 'العودة إلى الصفحة الرئيسية',
    popularTools: 'الأدوات الأكثر استخدامًا',
  },
  es: {
    title: 'Página no encontrada',
    body: 'Lo sentimos, pero la página que buscas no existe, fue eliminada o no está disponible temporalmente.',
    returnHome: 'Volver al Inicio',
    popularTools: 'Herramientas Populares',
  },
  ru: {
    title: 'Страница не найдена',
    body: 'К сожалению, страница, которую вы ищете, не существует, была удалена или временно недоступна.',
    returnHome: 'Вернуться на главную',
    popularTools: 'Популярные инструменты',
  },
  fr: {
    title: 'Page introuvable',
    body: "Nous sommes désolés, mais la page que vous recherchez n'existe pas, a été supprimée ou est temporairement indisponible.",
    returnHome: "Retour à l'accueil",
    popularTools: 'Outils populaires',
  },
} as const;

const POPULAR_TOOLS = [
  { href: 'background-remover', icon: Image, label: { en: 'Background Remover', ar: 'إزالة الخلفية', es: 'Removedor de Fondo', ru: 'Удаление фона', fr: "Suppresseur d'arrière-plan" } },
  { href: 'image-compressor', icon: Image, label: { en: 'Image Compressor', ar: 'ضاغط الصور', es: 'Compresor de Imágenes', ru: 'Компрессор изображений', fr: "Compresseur d'images" } },
  { href: 'json-formatter', icon: Code, label: { en: 'JSON Formatter', ar: 'منسّق JSON', es: 'Formateador de JSON', ru: 'Форматировщик JSON', fr: 'Formateur JSON' } },
  { href: 'meta-tag-generator', icon: Search, label: { en: 'Meta Tag Generator', ar: 'مولّد وسوم الميتا', es: 'Generador de Meta Tags', ru: 'Генератор мета-тегов', fr: 'Générateur de balises meta' } },
  { href: 'word-counter', icon: FileText, label: { en: 'Word Counter', ar: 'عداد الكلمات', es: 'Contador de Palabras', ru: 'Счётчик слов', fr: 'Compteur de mots' } },
] as const;

export default function NotFoundContent() {
  const pathname = usePathname();
  // Generic first-segment check against every structural locale (see
  // lib/i18n/config.ts) instead of hardcoding 'ar' by name, so adding a
  // locale there doesn't also require remembering to update this file.
  const firstSegment = pathname?.split('/')[1] ?? '';
  const locale: Locale = isValidLocale(firstSegment) ? firstSegment : defaultLocale;
  const t = T[locale];

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-16 text-center">

      <h1 className="text-5xl md:text-7xl font-extrabold text-foreground tracking-tight heading mb-4">
        404
      </h1>
      <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
        {t.title}
      </h2>

      <p className="text-textSecondary text-lg max-w-md mx-auto mb-10 leading-relaxed">
        {t.body}
      </p>

      <Link
        href={localizedHref(locale, '/')}
        className="inline-flex items-center gap-2 px-8 py-4 bg-accent hover:bg-accentHover text-white font-bold rounded-xl shadow-glow transition-all mb-12"
      >
        <Home className="w-5 h-5" />
        <span>{t.returnHome}</span>
      </Link>

      <div className="w-full max-w-lg">
        <p className="text-xs font-semibold text-textSecondary uppercase tracking-wider mb-4">{t.popularTools}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {POPULAR_TOOLS.map(({ href, icon: Icon, label }) => (
            <Link
              key={href}
              href={localizedHref(locale, `/${href}`)}
              className="flex items-center gap-3 px-4 py-3 bg-cardBg border border-borderDark rounded-xl text-sm font-semibold text-textSecondary hover:text-foreground hover:border-accent transition-all"
            >
              <Icon className="w-4 h-4 text-accent flex-shrink-0" />
              {label[locale]}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
